import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import RecruitGlanceLogo from "../components/RecruitGlanceLogo";

const PASSWORD_RULE_MESSAGE =
  "Password must be at least 8 characters and include one uppercase letter, one number, and one special character.";

const isStrongPassword = (value) =>
  value.length >= 8 &&
  /[A-Z]/.test(value) &&
  /\d/.test(value) &&
  /[^A-Za-z0-9]/.test(value);

export default function Register() {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showResend, setShowResend] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!isStrongPassword(password)) {
      setError(PASSWORD_RULE_MESSAGE);
      toast.error(PASSWORD_RULE_MESSAGE);
      return;
    }

    if (!acceptedPolicies) {
      const message = "Please accept the Terms, Privacy Policy, and AI Disclaimer to continue.";
      setError(message);
      toast.error(message);
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            first_name: firstName,
            last_name: lastName,
            company_name: companyName,
            email,
            password,
            accepted_terms: acceptedPolicies,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        const msg = Array.isArray(data.detail)
          ? data.detail[0].msg
          : data.detail || "Something went wrong";

        setError(msg);
        toast.error(msg);
        return;
      }

      navigate("/login", { state: { email } });
      toast.success("Account created! Check your email.");

      setShowResend(true);
    } catch (err) {
      setError("Server error. Try again.");
      toast.error("Server error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return toast.error("Enter email first");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/resend-verification`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        return toast.error(data.detail || "Failed to resend email");
      }

      toast.success("Verification email sent");

      setCooldown(30);
      const interval = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch {
      toast.error("Server error");
    }
  };

  return (
    <div className="min-h-screen bg-[#03140f] text-white">
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <RecruitGlanceLogo dark />
        <Link
          to="/login"
          className="rounded-full border border-emerald-300/30 px-4 py-2 text-sm font-semibold text-emerald-50 transition hover:border-emerald-200 hover:bg-white/10"
        >
          Sign in
        </Link>
      </header>

      <main className="mx-auto grid min-h-[calc(100vh-88px)] w-full max-w-6xl items-center gap-10 px-6 pb-16 pt-4 lg:grid-cols-[1fr_480px]">
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="max-w-2xl space-y-6"
        >
          <span className="inline-flex rounded-full border border-emerald-300/30 bg-emerald-300/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-100">
            Start with 5 free scans
          </span>
          <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
            Build your first shortlist without heavy ATS setup.
          </h1>
          <p className="max-w-xl text-base leading-7 text-emerald-50/75">
            Create your recruiter workspace, add jobs, import applicants, and
            use AI-assisted scoring to understand who deserves attention first.
          </p>
          <div className="rounded-3xl border border-emerald-300/20 bg-emerald-300/10 p-5">
            <p className="text-sm font-semibold text-emerald-50">
              RecruitGlance combines semantic matching, skill evidence, and
              recruiter-defined criteria.
            </p>
            <p className="mt-2 text-sm leading-6 text-emerald-50/65">
              Results are decision support, not automatic hiring decisions. You
              stay in control of review, notes, and shortlisting.
            </p>
          </div>
        </motion.section>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full rounded-3xl border border-white/10 bg-white p-6 text-slate-950 shadow-2xl shadow-black/30 sm:p-8"
        >
          <div className="mb-8 space-y-3 text-center">
            <h2 className="text-3xl font-semibold tracking-tight">Create your account</h2>
            <p className="text-sm leading-relaxed text-slate-500">
              Start hiring with AI-powered CV intelligence in seconds.
            </p>
          </div>

          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                type="text"
                placeholder="First name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-100"
              />

              <input
                type="text"
                placeholder="Last name"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-100"
              />
            </div>

            <input
              type="text"
              placeholder="Company name (optional)"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-100"
            />

            <input
              type="email"
              placeholder="Work email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-100"
            />

            <input
              type="password"
              placeholder="Password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-100"
            />
            <p className="text-xs leading-5 text-slate-500">
              Minimum 8 characters with one uppercase letter, one number, and one special character.
            </p>

            <label className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm leading-6 text-slate-700">
              <input
                type="checkbox"
                checked={acceptedPolicies}
                onChange={(e) => setAcceptedPolicies(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-emerald-300 accent-[#00533d]"
              />
              <span>
                I agree to the{" "}
                <Link to="/terms" className="font-semibold text-[#00533d] underline">
                  Terms of Use
                </Link>
                ,{" "}
                <Link to="/privacy" className="font-semibold text-[#00533d] underline">
                  Privacy Policy
                </Link>
                , and{" "}
                <Link to="/ai-disclaimer" className="font-semibold text-[#00533d] underline">
                  AI Disclaimer
                </Link>
                . I understand AI rankings and summaries can be wrong and must
                be reviewed before making hiring decisions.
              </span>
            </label>

            <button
              type="submit"
              disabled={loading || !acceptedPolicies}
              className="mt-2 w-full rounded-full bg-[#00533d] py-3 text-sm font-semibold text-white transition hover:bg-[#006f52] disabled:opacity-40"
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>

          {showResend && (
            <div className="mt-8 space-y-2 text-center">
              <p className="text-sm text-slate-500">Didn't receive email?</p>
              <button
                onClick={handleResend}
                disabled={cooldown > 0}
                className="text-sm font-semibold text-[#00533d] hover:underline disabled:opacity-40"
              >
                {cooldown > 0
                  ? `Resend in ${cooldown}s`
                  : "Resend verification email"}
              </button>
            </div>
          )}

          <p className="mt-10 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-[#00533d] hover:underline">
              Sign in
            </Link>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
